import React, { useMemo } from "react";

import type { TableInstance, ColumnInstance } from "react-table";
import { TableRow, TableBody, Fade, TableCell, Typography } from "@material-ui/core";
import type { IResponseWithLoading } from "App/Modules/Models";
import { Skeleton } from "@material-ui/lab";

import { useTableStyles } from "./TableStyles";

type TableLoadingSkeletonProps<T extends object> = {
    instance: TableInstance<T>;
    data: IResponseWithLoading<T[]>;
};

const avatarSkeleton = <Skeleton variant="circle" height={40} width={40} />;

const mainTextSkeleton = (classes: ReturnType<typeof useTableStyles>) => (
    <>
        <Typography variant="body1">
            <Skeleton className={classes.primarySkeletonCellRow} animation="wave" />
        </Typography>
        <Typography variant="body2">
            <Skeleton className={classes.secondarySkeletonCellRow} animation="wave" />
        </Typography>
    </>
);

const skeletonDot = (key: any) => <Skeleton key={key} variant="circle" height={3} width={3} />;

const nSkeletonDots = (nDots: number) => new Array(nDots).fill(null).map((_, i) => skeletonDot(i));

const numberSkeletonDots = (classes: ReturnType<typeof useTableStyles>) => (
    <div className={classes.skeletonDots}>{nSkeletonDots(3)}</div>
);

const longCellSkeletonDots = (classes: ReturnType<typeof useTableStyles>) => (
    <div className={classes.skeletonDots}>{nSkeletonDots(12)}</div>
);

const actionSkeleton = (classes: ReturnType<typeof useTableStyles>) => (
    <>
        <div className={classes.actionMenuSkeleton}>{nSkeletonDots(3)}</div>
    </>
);

const skeletonCellContent = (
    col: ColumnInstance<any>,
    colIndex: number,
    numVisibleCols: number,
    classes: ReturnType<typeof useTableStyles>,
) => {
    if (colIndex === 0 && typeof col.width === "number" && col.width < 100) {
        return avatarSkeleton;
    }
    if (colIndex === numVisibleCols - 1 && typeof col.width === "number" && col.width < 60) {
        return actionSkeleton(classes);
    }
    if (typeof col.width === "number" && col.width < 150) {
        return numberSkeletonDots(classes);
    }
    return mainTextSkeleton(classes);
};

export const partialLoadingCellContent = (
    col: ColumnInstance<any>,
    colIndex: number,
    numVisibleCols: number,
    classes: ReturnType<typeof useTableStyles>,
) => {
    if (colIndex === 0 && typeof col.width === "number" && col.width < 100) {
        return null;
    }
    if (colIndex === numVisibleCols - 1 && typeof col.width === "number" && col.width < 60) {
        return null;
    }
    if (typeof col.width === "number" && col.width < 150) {
        return numberSkeletonDots(classes);
    }
    return longCellSkeletonDots(classes);
};

const skeletonCell = (
    col: ColumnInstance<any>,
    colIndex: number,
    numVisibleCols: number,
    classes: ReturnType<typeof useTableStyles>,
) => (
    <TableCell component="div" {...col.getHeaderProps()} align={col.align}>
        {skeletonCellContent(col, colIndex, numVisibleCols, classes)}
    </TableCell>
);

export const TableLoadingSkeleton = <T extends object>({ instance, data }: TableLoadingSkeletonProps<T>) => {
    const classes = useTableStyles();

    const bodyRootClass = useMemo(
        () => (instance.plugins.some((p) => p.pluginName.includes("FlexLayout")) ? classes.flexRoot : ""),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [instance],
    );

    return (
        <TableBody component="div" className={bodyRootClass}>
            <Fade in={data.isLoading} timeout={{ enter: 400, exit: 400 }}>
                <div>
                    {
                        /* eslint-disable react/no-array-index-key */
                        Array(6)
                            .fill(null)
                            .map((_, index) => (
                                <TableRow component="div" key={index} className={classes.skeletonRow}>
                                    {instance.visibleColumns.map((col, colIndex) =>
                                        skeletonCell(col, colIndex, instance.visibleColumns.length, classes),
                                    )}
                                </TableRow>
                            ))
                        /* eslint-disable react/no-array-index-key */
                    }
                </div>
            </Fade>
        </TableBody>
    );
};
